import React, { useState, useRef } from 'react';
import * as tus from 'tus-js-client';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../components/Provider/CommonProvider';
import { FiUploadCloud, FiVideo, FiCheck, FiPause, FiPlay, FiTrash2, FiAlertCircle, FiLoader } from 'react-icons/fi';

/**
 * FileUploader — Direct-to-Bunny Stream upload
 * Flow: browser → napi (init, get TUS token) → browser → Bunny TUS directly
 * No local disk on server, no double bandwidth, full browser speed to Bunny edge.
 */
function FileUploader({ uploadType = 'video', cloneTo = null, actionType = 'created', videoId = null, seriesId = null, seasonId = null, nextHref = null }) {
  const { fetchRequest } = useMyContext();
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_Baseurl;

  const [files, setFiles] = useState([]);
  const [fileStatusMap, setFileStatusMap] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const uploadInstances = useRef({});
  const fileStatusRef = useRef({});

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files) {
      handleFileDrop(e.dataTransfer.files);
    }
  };

  const fileId = (file) => `${file.name}-${file.size}`;

  const updateStatus = (id, updates) => {
    setFileStatusMap(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
    fileStatusRef.current[id] = { ...(fileStatusRef.current[id] || {}), ...updates };
  };

  const fmtSpeed = (bps) => {
    if (bps > 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
    if (bps > 1024) return `${(bps / 1024).toFixed(1)} KB/s`;
    return `${bps.toFixed(0)} B/s`;
  };

  const uploadFile = async (file) => {
    const id = fileId(file);
    updateStatus(id, { status: 'initializing', progress: 0, speed: '', error: '' });

    // Always read token directly from localStorage so it works even when the
    // Provider's accessToken state is stale (e.g. first SPA login without page reload)
    const authHeaders = (() => {
      const t = localStorage.getItem('access_token');
      return t ? { Authorization: `Bearer ${t}` } : {};
    })();

    // Step 1: Ask napi to create a Bunny Stream slot and return TUS credentials
    let initData;
    try {
      const res = await fetchRequest({
        method: 'POST',
        url: `${baseURL}/admin/library/bunny-stream-init`,
        data: { fileName: file.name, fileSize: file.size },
        headers: authHeaders,
      });
      if (!res?.status) throw new Error(res?.message || 'Init failed');
      initData = res;
    } catch (err) {
      updateStatus(id, { status: 'error', error: err.message });
      return;
    }

    const { videoGuid, libraryId, authorizationSignature, authorizationExpire, dbId } = initData;
    updateStatus(id, { status: 'uploading' });

    let lastLoaded = 0;
    let lastTime = Date.now();

    // Step 2: Upload directly from browser to Bunny TUS endpoint
    const upload = new tus.Upload(file, {
      endpoint: 'https://video.bunnycdn.com/tusupload',
      retryDelays: [0, 1000, 3000, 5000, 10000, 30000],
      headers: {
        AuthorizationSignature: authorizationSignature,
        AuthorizationExpire: String(authorizationExpire),
        VideoId: videoGuid,
        LibraryId: String(libraryId),
      },
      metadata: {
        filename: file.name,
        filetype: file.type || 'video/mp4',
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const now = Date.now();
        const elapsed = (now - lastTime) / 1000;
        const speed = elapsed > 0.2 ? (bytesUploaded - lastLoaded) / elapsed : 0;
        if (elapsed > 0.2) { lastLoaded = bytesUploaded; lastTime = now; }
        updateStatus(id, {
          progress: Math.round((bytesUploaded / bytesTotal) * 100),
          speed: speed > 0 ? fmtSpeed(speed) : fileStatusRef.current[id]?.speed || '',
        });
      },
      onSuccess: async () => {
        updateStatus(id, { status: 'processing', progress: 100, speed: '' });
        // Step 3: Notify napi upload is done so it can finalize the DB record
        let officialId = null;
        try {
          const completeRes = await fetchRequest({
            method: 'POST',
            url: `${baseURL}/admin/library/bunny-stream-complete`,
            data: {
              dbId,
              title: file.name,
              clone_to: cloneTo,
              action_type: actionType,
              upload_type: uploadType,
              video_id: videoId,
              series_id: seriesId,
              season_id: seasonId,
            },
            headers: authHeaders,
          });
          // Use officialId (video/episode DB id) when available; fall back to
          // the library record id for uploads that have no clone_to target.
          officialId = completeRes?.officialId ?? completeRes?.id ?? null;
          console.log('[FileUploader] bunny-stream-complete response:', completeRes);
        } catch (err) {
          console.error('[FileUploader] bunny-stream-complete error:', err);
        }
        updateStatus(id, { status: 'done', officialId });
      },
      onError: (error) => {
        if (fileStatusRef.current[id]?.status !== 'cancelled') {
          updateStatus(id, { status: 'error', error: error.message, speed: '' });
        }
      },
    });

    uploadInstances.current[id] = upload;
    upload.start();
  };

  const cancelUpload = (file) => {
    const id = fileId(file);
    uploadInstances.current[id]?.abort();
    updateStatus(id, { status: 'cancelled', speed: '' });
  };

  const retryUpload = (file) => {
    const id = fileId(file);
    const instance = uploadInstances.current[id];
    if (instance) {
      // tus resume: find previous offset and continue
      updateStatus(id, { status: 'uploading', error: '' });
      instance.start();
    } else {
      uploadFile(file);
    }
  };

  const removeFile = (file) => {
    const id = fileId(file);
    uploadInstances.current[id]?.abort();
    setFiles(prev => prev.filter(f => fileId(f) !== id));
    setFileStatusMap(prev => prev.filter(item => item.id !== id));
    delete uploadInstances.current[id];
    delete fileStatusRef.current[id];
  };

  const handleFileDrop = (droppedFiles) => {
    const newFiles = Array.from(droppedFiles).filter(f => f.type.startsWith('video/') || /\.(mp4|mov|avi|mkv|webm)$/i.test(f.name));
    if (!newFiles.length) return;
    setFiles(prev => [...prev, ...newFiles]);
    newFiles.forEach(file => {
      setFileStatusMap(prev => [...prev, { id: fileId(file), status: 'idle', progress: 0, speed: '', error: '' }]);
      uploadFile(file);
    });
  };

  const fileInputRef = useRef();

  const statusColor = (status) => {
    if (status === 'error') return '#e53935';
    if (status === 'done') return '#43a047';
    if (status === 'processing') return '#fb8c00';
    return '#2196f3';
  };

  return (
    <div className="premium-uploader-container">
      {/* Drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`premium-dropzone ${isDragging ? 'is-dragging' : ''}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="video/*,.mp4,.avi,.mov,.mkv,.webm"
          onChange={(e) => handleFileDrop(e.target.files)}
          style={{ display: 'none' }}
        />
        <div className="premium-dropzone-icon-wrapper">
          <FiUploadCloud className="premium-dropzone-icon" />
        </div>
        <h3 className="premium-dropzone-title">Drop videos here or click to browse</h3>
        <p className="premium-dropzone-subtitle">For better uploading experience</p>
        
        <div className="premium-dropzone-specs">
          <div className="spec-tag">100 GB per file</div>
          <div className="spec-tag">Unlimited parallel uploads</div>
          <div className="spec-tag">Auto-transcoded</div>
          <div className="spec-tag">Delivered worldwide via CDN</div>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="premium-file-list">
          <div className="premium-file-list-header">
            <h4>Upload Queue ({files.length})</h4>
          </div>
          <div className="premium-file-list-items">
            {files.map(file => {
              const id = fileId(file);
              const { progress = 0, status = 'idle', error = '', speed = '', officialId = null } = fileStatusMap.find(i => i.id === id) || {};
              const sizeMB = (file.size / (1024 * 1024)).toFixed(1);

              return (
                <div key={id} className={`premium-file-card status-${status}`}>
                  <div className="premium-file-info">
                    <div className="file-icon-wrapper">
                      <FiVideo className="file-icon" />
                    </div>
                    <div className="file-metadata">
                      <span className="file-name" title={file.name}>{file.name}</span>
                      <span className="file-size">{sizeMB} MB</span>
                    </div>
                    <div className="file-actions">
                      {status === 'uploading' && (
                        <button onClick={() => cancelUpload(file)} className="btn-action pause" title="Pause">
                          <FiPause />
                        </button>
                      )}
                      {(status === 'cancelled' || status === 'error') && (
                        <button onClick={() => retryUpload(file)} className="btn-action resume" title="Resume / Retry">
                          <FiPlay />
                        </button>
                      )}
                      {['done', 'cancelled', 'error'].includes(status) && (
                        <button onClick={() => removeFile(file)} className="btn-action remove" title="Remove">
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress bar and details */}
                  <div className="premium-progress-container">
                    <div className="premium-progress-bar-wrapper">
                      <div
                        className={`premium-progress-bar ${status}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>

                    <div className="premium-progress-details">
                      <span className="progress-status-text">
                        {status === 'initializing' && (
                          <><FiLoader className="spin me-1" /> Initializing...</>
                        )}
                        {status === 'uploading' && (
                          <><FiLoader className="spin me-1" /> Uploading {progress}%</>
                        )}
                        {status === 'processing' && (
                          <><FiLoader className="spin me-1 animate-pulse" /> Processing...</>
                        )}
                        {status === 'done' && (
                          <span className="d-flex align-items-center gap-2">
                            <FiCheck className="me-1 text-success-g" /> Completed
                            {nextHref && officialId && (
                              <a
                                href={nextHref(officialId)}
                                className="btn btn-sm btn-primary ms-2 px-3 py-1"
                                style={{ fontSize: '0.78rem', textDecoration: 'none' }}
                                onClick={(e) => {
                                  if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                                    e.preventDefault();
                                    navigate(nextHref(officialId));
                                  }
                                }}
                              >
                                Next →
                              </a>
                            )}
                          </span>
                        )}
                        {status === 'cancelled' && (
                          <span className="text-warning-g">Paused</span>
                        )}
                        {status === 'error' && (
                          <span className="text-error-g" title={error}><FiAlertCircle className="me-1" /> Failed</span>
                        )}
                        {status === 'idle' && 'Queued'}
                      </span>
                      {speed && status === 'uploading' && (
                        <span className="progress-speed">{speed}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUploader;
